import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { AudioRecorder } from '@/lib/audio-recorder'

interface VoiceNoteRecorderProps {
  contactId: string
  onSaved?: () => void
  onCancel?: () => void
}

type RecorderState = 'idle' | 'recording' | 'preview' | 'uploading' | 'done'

export function VoiceNoteRecorder({ contactId, onSaved, onCancel }: VoiceNoteRecorderProps) {
  const { t } = useTranslation()
  const [state, setState] = useState<RecorderState>('idle')
  const [elapsed, setElapsed] = useState(0)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [audioDuration, setAudioDuration] = useState(0)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [transcript, setTranscript] = useState<string | null>(null)
  const [transcribing, setTranscribing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [noteId, setNoteId] = useState<string | null>(null)

  const recorderRef = useRef<AudioRecorder>(null!)
  if (!recorderRef.current) {
    recorderRef.current = new AudioRecorder()
  }
  const timerRef = useRef<ReturnType<typeof setInterval>>(undefined)
  const audioRef = useRef<HTMLAudioElement>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      if (audioUrl) URL.revokeObjectURL(audioUrl)
    }
  }, [audioUrl])

  const startRecording = useCallback(async () => {
    try {
      setError(null)
      await recorderRef.current.start()
      setState('recording')
      setElapsed(0)
      timerRef.current = setInterval(() => {
        setElapsed(recorderRef.current.getElapsedSeconds())
      }, 500)
    } catch {
      setError(t('voiceNote.micError', 'Microphone access denied'))
    }
  }, [t])

  const stopRecording = useCallback(async () => {
    if (timerRef.current) clearInterval(timerRef.current)

    try {
      const result = await recorderRef.current.stop()
      setAudioBlob(result.blob)
      setAudioDuration(result.durationSeconds)
      const url = URL.createObjectURL(result.blob)
      setAudioUrl(url)
      setState('preview')
    } catch {
      setError(t('voiceNote.recordError', 'Recording failed'))
      setState('idle')
    }
  }, [t])

  const discard = useCallback(() => {
    if (audioUrl) URL.revokeObjectURL(audioUrl)
    setAudioBlob(null)
    setAudioUrl(null)
    setTranscript(null)
    setNoteId(null)
    setElapsed(0)
    setState('idle')
  }, [audioUrl])

  const upload = useCallback(async () => {
    if (!audioBlob) return

    setState('uploading')
    setTranscribing(true)

    try {
      const formData = new FormData()
      formData.append('audio', audioBlob, 'recording.webm')
      formData.append('duration', String(audioDuration))

      const res = await fetch(`/api/contacts/${contactId}/voice-notes`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })

      if (!res.ok) {
        throw new Error('Upload failed')
      }

      const note = await res.json()
      setNoteId(note.id)
      setState('done')

      // Poll for transcription
      pollTranscript(note.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('voiceNote.uploadError', 'Upload failed'))
      setState('preview')
      setTranscribing(false)
    }
  }, [audioBlob, audioDuration, contactId, t])

  const pollTranscript = useCallback(async (id: string) => {
    const maxAttempts = 30
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((r) => setTimeout(r, 2000))

      try {
        const res = await fetch(`/api/voice-notes/${id}`, {
          credentials: 'include',
        })
        if (!res.ok) break

        const note = await res.json()
        if (note.status === 'completed') {
          setTranscript(note.transcript || '')
          setTranscribing(false)
          return
        }
        if (note.status === 'failed') {
          setTranscribing(false)
          return
        }
      } catch {
        break
      }
    }
    setTranscribing(false)
  }, [])

  const saveTranscript = useCallback(async () => {
    if (!noteId || transcript === null) return

    await fetch(`/api/voice-notes/${noteId}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transcript }),
    })

    onSaved?.()
  }, [noteId, transcript, onSaved])

  const appendToNotes = useCallback(async () => {
    if (!transcript) return

    // Append transcript to contact notes
    const contactRes = await fetch(`/api/contacts/${contactId}`, {
      credentials: 'include',
    })
    if (!contactRes.ok) return

    const contact = await contactRes.json()
    const existingNotes = contact.notes || ''
    const timestamp = new Date().toLocaleDateString()
    const updatedNotes = existingNotes
      ? `${existingNotes}\n\n---\nVoice note (${timestamp}):\n${transcript}`
      : `Voice note (${timestamp}):\n${transcript}`

    await fetch(`/api/contacts/${contactId}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes: updatedNotes }),
    })
  }, [contactId, transcript])

  function formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${String(s).padStart(2, '0')}`
  }

  return (
    <div className="bg-card flex flex-col gap-4 rounded-lg border p-4">
      {error && (
        <div className="bg-destructive/10 text-destructive rounded p-2 text-sm">{error}</div>
      )}

      {/* Recording controls */}
      {(state === 'idle' || state === 'recording') && (
        <div className="flex flex-col items-center gap-4">
          <div className="font-mono text-3xl tabular-nums">{formatTime(elapsed)}</div>

          {state === 'recording' && (
            <div className="flex items-center gap-2">
              <div className="bg-coral h-3 w-3 animate-pulse rounded-full" />
              <span className="text-muted-foreground text-sm">
                {t('voiceNote.recording', 'Recording...')}
              </span>
            </div>
          )}

          <button
            onClick={state === 'idle' ? startRecording : stopRecording}
            className={`h-16 w-16 rounded-full transition-colors ${
              state === 'recording'
                ? 'bg-coral hover:bg-coral/90'
                : 'bg-primary hover:bg-primary/90'
            }`}
          >
            {state === 'recording' ? (
              <div className="mx-auto h-6 w-6 rounded bg-white" />
            ) : (
              <div className="mx-auto h-6 w-6 rounded-full bg-white" />
            )}
          </button>
        </div>
      )}

      {/* Preview / playback */}
      {(state === 'preview' || state === 'done') && audioUrl && (
        <div className="flex flex-col gap-3">
          <audio ref={audioRef} src={audioUrl} controls className="w-full" />

          <div className="text-muted-foreground text-sm">
            {t('voiceNote.duration', 'Duration')}: {formatTime(audioDuration)}
          </div>
        </div>
      )}

      {/* Transcript */}
      {state === 'done' && (
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">{t('voiceNote.transcript', 'Transcript')}</label>
          {transcribing ? (
            <div className="bg-muted text-muted-foreground rounded border p-3 text-sm">
              {t('voiceNote.transcribing', 'Transcribing...')}
            </div>
          ) : (
            <textarea
              value={transcript || ''}
              onChange={(e) => setTranscript(e.target.value)}
              rows={4}
              className="bg-background rounded border p-3 text-sm"
            />
          )}

          {transcript && (
            <button onClick={appendToNotes} className="text-primary self-start text-sm font-medium">
              {t('voiceNote.addToNotes', 'Add to contact notes')}
            </button>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        {state === 'preview' && (
          <>
            <button onClick={discard} className="flex-1 rounded-lg border p-2 text-sm font-medium">
              {t('voiceNote.discard', 'Discard')}
            </button>
            <button
              onClick={upload}
              className="bg-primary text-primary-foreground flex-1 rounded-lg p-2 text-sm font-medium"
            >
              {t('voiceNote.save', 'Save & Transcribe')}
            </button>
          </>
        )}

        {state === 'done' && !transcribing && (
          <>
            <button
              onClick={() => {
                saveTranscript()
              }}
              className="bg-primary text-primary-foreground flex-1 rounded-lg p-2 text-sm font-medium"
            >
              {t('voiceNote.done', 'Done')}
            </button>
          </>
        )}

        {(state === 'idle' || state === 'preview' || state === 'done') && onCancel && (
          <button onClick={onCancel} className="rounded-lg border px-4 py-2 text-sm">
            {t('common.cancel', 'Cancel')}
          </button>
        )}
      </div>

      {state === 'uploading' && (
        <div className="text-muted-foreground text-center text-sm">
          {t('voiceNote.uploading', 'Uploading...')}
        </div>
      )}
    </div>
  )
}
